import { useCallback, useState } from "react";
import {
  Permission,
  useDeleteUserInviteMutation,
  useGenerateSignInLinkMutation,
  useSendInviteEmailMutation,
  useUpdateUserMutation,
} from "store/api/generatedApi";
import { fetchErrorDetail } from "helpers";
import { useConfirmDialog } from "contexts/confirmDialog";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { UserRow } from "./userAdminFilters";

export interface UserUpdateFields {
  uid: string;
  is_admin?: boolean;
  is_enabled?: boolean;
  permissions?: Permission[];
}

export const useUserAdminActions = ({
  tenant,
  userById,
}: {
  tenant: string;
  userById: Map<string, UserRow>;
}) => {
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const confirm = useConfirmDialog();

  const [updateUser, { isLoading: isSavingUser }] = useUpdateUserMutation();
  const [generateSignInLink] = useGenerateSignInLinkMutation();
  const [sendInviteEmail] = useSendInviteEmailMutation();
  const [deleteInvite] = useDeleteUserInviteMutation();

  const [signInLink, setSignInLink] = useState<string | null>(null);
  const [isBulkBusy, setIsBulkBusy] = useState(false);

  const notifyError = useCallback(
    (error: unknown, fallback: string) => {
      notify({ type: "error", text: fetchErrorDetail(error) ?? fallback });
    },
    [notify]
  );

  const submitUserUpdate = useCallback(
    async (update: UserUpdateFields) => {
      try {
        await updateUser({
          tenantName: tenant,
          userUpdate: {
            uid: update.uid,
            is_admin: update.is_admin,
            is_enabled: update.is_enabled,
            permissions: update.permissions
              ? { [tenant]: update.permissions }
              : undefined,
          },
        }).unwrap();
        notify({ type: "success", text: t("changesSaved") });
      } catch (error) {
        notifyError(error, t("couldntUpdateUser"));
        throw error;
      }
    },
    [updateUser, tenant, notify, notifyError, t]
  );

  const runBulk = useCallback(
    async (
      items: string[],
      operation: (item: string) => Promise<unknown>,
      successText: string
    ) => {
      setIsBulkBusy(true);
      let failures = 0;
      for (const item of items) {
        try {
          await operation(item);
        } catch {
          failures += 1;
        }
      }
      setIsBulkBusy(false);
      notify(
        failures > 0
          ? {
              type: "warning",
              text: t("bulkCompletedWithFailures", { count: failures }),
            }
          : { type: "success", text: successText }
      );
    },
    [notify, t]
  );

  const bulkSetEnabled = useCallback(
    (uids: string[], isEnabled: boolean) =>
      runBulk(
        uids,
        (uid) =>
          updateUser({
            tenantName: tenant,
            userUpdate: { uid, is_enabled: isEnabled },
          }).unwrap(),
        t("changesSaved")
      ),
    [runBulk, updateUser, tenant, t]
  );

  const bulkSetPermission = useCallback(
    (uids: string[], permission: Permission, grant: boolean) =>
      runBulk(
        uids,
        async (uid) => {
          const user = userById.get(uid);
          if (!user) return;
          const current = user.permissions.map((entry) => entry.permission);
          const has = current.includes(permission);
          if (grant === has) return;
          const next = grant
            ? [...current, permission]
            : current.filter((entry) => entry !== permission);
          await updateUser({
            tenantName: tenant,
            userUpdate: { uid, permissions: { [tenant]: next } },
          }).unwrap();
        },
        t("changesSaved")
      ),
    [runBulk, userById, updateUser, tenant, t]
  );

  const bulkResendInvites = useCallback(
    async (emails: string[]) => {
      const confirmed = await confirm({
        title: t("resendInvites"),
        message: t("confirmResendInvites", { count: emails.length }),
      });
      if (!confirmed) return;
      await runBulk(
        emails,
        (email) =>
          sendInviteEmail({
            tenantName: tenant,
            sendInviteEmailRequest: { email },
          }).unwrap(),
        t("inviteEmailsSent")
      );
    },
    [confirm, runBulk, sendInviteEmail, tenant, t]
  );

  const bulkDeleteInvites = useCallback(
    (emails: string[]) =>
      runBulk(
        emails,
        (email) => deleteInvite({ tenantName: tenant, email }).unwrap(),
        t("invitesDeleted")
      ),
    [runBulk, deleteInvite, tenant, t]
  );

  const createInviteLink = useCallback(
    (email: string) => {
      generateSignInLink({
        tenantName: tenant,
        generateSignInLinkRequest: { email },
      })
        .unwrap()
        .then((response) => {
          setSignInLink(response.sign_in_link);
        })
        .catch((error: unknown) => {
          notifyError(error, t("couldntGenerateLink"));
        });
    },
    [generateSignInLink, tenant, notifyError, t]
  );

  const sendInvite = useCallback(
    async (email: string) => {
      const confirmed = await confirm({
        title: t("sendInviteEmail"),
        message: t("confirmSendInviteEmail", { email }),
      });
      if (!confirmed) return;
      try {
        await sendInviteEmail({
          tenantName: tenant,
          sendInviteEmailRequest: { email },
        }).unwrap();
        notify({ type: "success", text: t("inviteEmailSent") });
      } catch (error) {
        notifyError(error, t("couldntSendInviteEmail"));
      }
    },
    [confirm, sendInviteEmail, tenant, notify, notifyError, t]
  );

  const removeInvite = useCallback(
    async (email: string) => {
      try {
        await deleteInvite({ tenantName: tenant, email }).unwrap();
      } catch (error) {
        notifyError(error, t("couldntDeleteInvite"));
      }
    },
    [deleteInvite, tenant, notifyError, t]
  );

  return {
    isSavingUser,
    isBulkBusy,
    signInLink,
    clearSignInLink: useCallback(() => {
      setSignInLink(null);
    }, []),
    submitUserUpdate,
    createInviteLink,
    sendInvite,
    removeInvite,
    bulkSetEnabled,
    bulkSetPermission,
    bulkResendInvites,
    bulkDeleteInvites,
  };
};
